/**
 * frontend/src/pages/CheckIns.jsx
 * =================================
 * Check-in history and staff token management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { checkinsApi } from '../lib/api'
import { format } from 'date-fns'

export default function CheckIns() {
  const qc = useQueryClient()
  const [showTokenModal, setShowTokenModal] = useState(false)
  const [newStaffName, setNewStaffName] = useState('')
  const [createdToken, setCreatedToken] = useState(null)

  const { data: checkins = [], isLoading } = useQuery({
    queryKey: ['checkins'],
    queryFn: () => checkinsApi.list({ limit: 100 }).then((r) => r.data),
  })

  const { data: tokens = [] } = useQuery({
    queryKey: ['staff-tokens'],
    queryFn: () => checkinsApi.listTokens().then((r) => r.data),
  })

  const createTokenMutation = useMutation({
    mutationFn: (data) => checkinsApi.createToken(data),
    onSuccess: (res) => {
      qc.invalidateQueries(['staff-tokens'])
      setCreatedToken(res.data)
      setNewStaffName('')
    },
  })

  const deactivateTokenMutation = useMutation({
    mutationFn: (id) => checkinsApi.deactivateToken(id),
    onSuccess: () => qc.invalidateQueries(['staff-tokens']),
  })

  const checkinUrl = (token) =>
    `${window.location.origin}/checkin?token=${token}`

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-white">Check-Ins</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Staff submissions and token management
          </p>
        </div>
        <button
          onClick={() => { setShowTokenModal(true); setCreatedToken(null) }}
          className="px-4 py-2 bg-violet-700 hover:bg-violet-600 text-white text-sm rounded-lg font-medium transition-colors"
        >
          + New Staff Token
        </button>
      </div>

      {/* Active tokens */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl mb-6 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">Staff Check-In Links</h2>
        </div>
        {tokens.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm">
            No staff tokens yet. Create one to give staff check-in access.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800">
                <th className="px-5 py-3 text-left font-medium">Staff Name</th>
                <th className="px-5 py-3 text-left font-medium">Token (partial)</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Created</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {tokens.map((t) => (
                <tr key={t.id} className="hover:bg-gray-800/30">
                  <td className="px-5 py-3 text-white font-medium">{t.staff_name}</td>
                  <td className="px-5 py-3 text-gray-500 font-mono text-xs">
                    {t.token.slice(0, 12)}…
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.is_active ? 'bg-green-900/50 text-green-400' : 'bg-gray-800 text-gray-500'
                    }`}>
                      {t.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    {format(new Date(t.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => navigator.clipboard.writeText(checkinUrl(t.token))}
                      className="text-xs text-violet-400 hover:text-violet-300 mr-3"
                    >
                      Copy URL
                    </button>
                    {t.is_active && (
                      <button
                        onClick={() => { if (confirm(`Revoke token for ${t.staff_name}?`)) deactivateTokenMutation.mutate(t.id) }}
                        className="text-xs text-red-500 hover:text-red-400"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Check-in history */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">Recent Check-Ins</h2>
        </div>
        {isLoading ? (
          <div className="p-5 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-800 rounded animate-pulse" />
            ))}
          </div>
        ) : checkins.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-500 text-sm">
            No check-ins yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800">
                <th className="px-5 py-3 text-left font-medium">Partner</th>
                <th className="px-4 py-3 text-left font-medium">Staff</th>
                <th className="px-4 py-3 text-right font-medium">Guests</th>
                <th className="px-4 py-3 text-left font-medium">Notes</th>
                <th className="px-5 py-3 text-right font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {checkins.map((ci) => (
                <tr key={ci.id} className="hover:bg-gray-800/30">
                  <td className="px-5 py-3 text-white">{ci.partner_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-400">{ci.staff_name}</td>
                  <td className="px-4 py-3 text-right text-gray-300 font-semibold">{ci.guest_count}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{ci.notes || '—'}</td>
                  <td className="px-5 py-3 text-right text-gray-500 text-xs">
                    {format(new Date(ci.checked_in_at), 'MMM d, h:mm a')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New token modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6">
            <h2 className="font-semibold text-white mb-4">Create Staff Check-In Token</h2>

            {createdToken ? (
              <div>
                <p className="text-sm text-gray-400 mb-3">
                  Token created for <strong className="text-white">{createdToken.staff_name}</strong>.
                  Share this URL — it won't be shown again in full.
                </p>
                <div className="bg-gray-800 rounded-lg p-3 font-mono text-xs text-green-300 break-all mb-4">
                  {checkinUrl(createdToken.token)}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(checkinUrl(createdToken.token))}
                  className="w-full bg-violet-700 hover:bg-violet-600 text-white text-sm py-2.5 rounded-lg mb-2"
                >
                  Copy URL
                </button>
                <button
                  onClick={() => { setShowTokenModal(false); setCreatedToken(null) }}
                  className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm py-2.5 rounded-lg"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Staff Name</label>
                  <input
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                    value={newStaffName}
                    onChange={(e) => setNewStaffName(e.target.value)}
                    placeholder="Maria — Tasting Room"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => createTokenMutation.mutate({ staff_name: newStaffName })}
                    disabled={!newStaffName.trim() || createTokenMutation.isPending}
                    className="flex-1 bg-violet-700 hover:bg-violet-600 disabled:opacity-40 text-white text-sm py-2.5 rounded-lg font-medium"
                  >
                    Create Token
                  </button>
                  <button
                    onClick={() => setShowTokenModal(false)}
                    className="px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
